import { useMemo } from "react";
import { LocalBusinessJsonLd } from "next-seo";
import { useBusinessProfile } from "hooks/useFetch";
import useSortWorkingHours from "hooks/useSortWorkingHours";
import useTranslation from "next-translate/useTranslation";
import { uniq, upperFirst } from "lodash-es";

const DOMAIN_URL = process.env.NEXT_PUBLIC_DOMAIN_URL;

const StoreJsonLd = () => {
  const { t } = useTranslation("common");
  const { data: businessProfile } = useBusinessProfile();
  const profile = businessProfile?.data;
  const location = profile?.locations.items[0].address;
  const workingHours = profile?.workingHours;
  const review = profile?.reviews.items;
  const service = profile?.services.items;
  const locations = profile?.locations.items;
  const images = profile?.coverImages.map((x) => x.url);
  const description = profile?.description || t("sections.about.description");
  const sameAs = Object.values(profile?.publicLinks ?? {});

  const sorting = useSortWorkingHours(workingHours);
  const openingHours = useMemo(
    () =>
      sorting.map((x) => ({
        opens: x.time?.[0]?.start || "00:00",
        closes: x.time?.[0]?.end || "00:00",
        dayOfWeek: upperFirst(x.d),
      })),
    [sorting]
  );

  const reviews = useMemo(
    () =>
      review?.map((re) => ({
        author: `${re.author.firstName} ${re.author.lastName}`,
        datePublished: re.publishedAt,
        reviewBody: re.comment,
        reviewRating: {
          ratingValue: re.score,
        },
      })),
    [review]
  );

  const areas = useMemo(
    () =>
      locations?.map((loc) => ({
        geoMidpoint: {
          latitude: loc.address.geoLocation.latitude,
          longitude: loc.address.geoLocation.longitude,
        },
        geoRadius: 1000,
      })),
    [locations]
  );

  const { makesOffer, priceRange } = useMemo(() => {
    const makesOffer = service?.map((se) => ({
      priceSpecification: {
        type: "UnitPriceSpecification",
        priceCurrency: profile?.currency.name,
        price: `${se.price}`,
      },
      itemOffered: {
        name: se.name,
        description: se.description,
      },
    }));

    const prices = uniq(
      makesOffer
        ?.map((offer) => Number(offer.priceSpecification.price))
        ?.filter((price) => Number.isFinite(price))
    );
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    let priceRange = null;
    if (prices.length) {
      priceRange =
        minPrice === maxPrice
          ? `${profile?.currency.name} ${profile?.currency.symbol}${minPrice}`
          : `${profile?.currency.name} ${profile?.currency.symbol}${minPrice} - ${profile?.currency.symbol}${maxPrice}`;
    }

    return { makesOffer, priceRange };
  }, [profile?.currency.name, profile?.currency.symbol, service]);

  return (
    <LocalBusinessJsonLd
      type="LocalBusiness"
      geo={{
        "@type": "GeoCoordinates",
        latitude: location?.geoLocation.latitude,
        longitude: location?.geoLocation.longitude,
      }}
      id={`${DOMAIN_URL}/b/${profile?.key}`}
      name={profile?.name || ""}
      description={description || ""}
      url={`${DOMAIN_URL}/b/${profile?.key}`}
      telephone={`+${profile?.phoneNumber?.countryCode}${
        profile?.phoneNumber?.number || ""
      }`}
      address={{
        "@type": "PostalAddress",
        streetAddress: location?.displayAddress,
        addressLocality: location?.city,
        addressRegion: location?.state,
        postalCode: location?.postalCode,
        addressCountry: location?.country,
      }}
      saveAs={sameAs}
      images={images}
      openingHours={openingHours}
      {...(priceRange ? { priceRange } : {})}
      {...(profile && profile?.rate?.count > 0
        ? {
            rating: {
              "@type": "AggregateRating",
              ratingValue: `${profile?.rate?.score}`,
              ratingCount: `${profile?.rate?.count}`,
              bestRating: 5,
              worstRating: 1,
            },
          }
        : {})}
      review={reviews}
      makesOffer={makesOffer}
      areaServed={areas}
    />
  );
};

export { StoreJsonLd };
